import { useNavigate } from 'react-router-dom'
import { Globe2, AlertTriangle, Copy, FileX, FileSearch, Banknote } from 'lucide-react'
import { PageHeader } from '@/components/ui/PageHeader'
import { CenterSpinner } from '@/components/ui/Spinner'
import { Tabs, type TabSpec } from '@/components/ui/Tabs'
import { Badge } from '@/components/ui/Badge'
import { Table, THead, TBody, TR, TH, TD } from '@/components/ui/Table'
import { EmptyState } from '@/components/ui/EmptyState'
import { useDiscrepancies } from '@/lib/api/hooks'
import type { DiscrepancyIssue, DiscrepancyType } from '@/lib/api/types'
import { dateTime } from '@/lib/format'

type TabKey = 'all' | DiscrepancyType

const TAB_LABEL: Record<TabKey, string> = {
  all: 'Mind',
  nav_only: 'NAV-ban van, dokumentum nincs',
  doc_only: 'Dokumentum van, NAV-ban nincs',
  amount_mismatch: 'Összegeltérés',
  duplicate_suspect: 'Duplikáció gyanú',
  foreign_no_nav: 'Külföldi (NAV nélkül)',
  number_mismatch: 'Számlaszám eltér',
}

const TAB_ICON: Record<TabKey, React.ComponentType<{ className?: string }>> = {
  all: AlertTriangle,
  nav_only: FileX,
  doc_only: FileSearch,
  amount_mismatch: Banknote,
  duplicate_suspect: Copy,
  foreign_no_nav: Globe2,
  number_mismatch: FileSearch,
}

export function DiscrepanciesPage() {
  const { data, isLoading } = useDiscrepancies()

  if (isLoading) return <CenterSpinner />

  const all = data ?? []

  function filterFor(type: TabKey): DiscrepancyIssue[] {
    if (type === 'all') return all
    return all.filter((d) => d.type === type)
  }

  const tabs: TabSpec[] = (Object.keys(TAB_LABEL) as TabKey[]).map((k) => {
    const items = filterFor(k)
    const Icon = TAB_ICON[k]
    return {
      key: k,
      label: (
        <span className="inline-flex items-center gap-1.5">
          <Icon className="h-3.5 w-3.5" /> {TAB_LABEL[k]}
        </span>
      ),
      badge: items.length > 0 ? <Badge tone="neutral">{items.length}</Badge> : null,
      content: <DiscrepancyTable items={items} />,
    }
  })

  return (
    <div>
      <PageHeader
        title="Eltérések"
        subtitle="Finance team napi munkalapja — a felszínre került anomáliák itt rendezhetők"
      />
      <Tabs tabs={tabs} initial="all" />
    </div>
  )
}

function DiscrepancyTable({ items }: { items: DiscrepancyIssue[] }) {
  const nav = useNavigate()

  if (items.length === 0) {
    return <EmptyState title="Nincs ebbe a kategóriába tartozó tétel" description="Friss állapot." />
  }

  return (
    <Table>
      <THead>
        <TR>
          <TH>Szállító</TH>
          <TH>Számlaszám</TH>
          <TH>Mi tér el?</TH>
          <TH>Észlelve</TH>
          <TH align="right">Akció</TH>
        </TR>
      </THead>
      <TBody>
        {items.map((it) => (
          <TR key={it.invoiceId + it.type} onClick={() => nav(`/invoices/${it.invoiceId}`)}>
            <TD>{it.supplierName ?? '—'}</TD>
            <TD className="font-mono text-xs">{it.invoiceNumber ?? '—'}</TD>
            <TD>{it.summary}</TD>
            <TD>{dateTime(it.detectedAt)}</TD>
            <TD align="right">
              <span className="text-brand-600 text-xs hover:underline">Megnyitás →</span>
            </TD>
          </TR>
        ))}
      </TBody>
    </Table>
  )
}
